<script setup lang="ts">
import type { Editor as CoreEditor } from '@tiptap/core';
import Color from '@tiptap/extension-color';
import {
    Table,
    TableCell,
    TableHeader,
    TableRow,
} from '@tiptap/extension-table';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import StarterKit from '@tiptap/starter-kit';
import { EditorContent, useEditor } from '@tiptap/vue-3';
import { computed, onBeforeUnmount, provide, readonly, ref, watch } from 'vue';
import InsertExtractionTableDialog from '@/components/editor/InsertExtractionTableDialog.vue';
import InsertFigureDialog from '@/components/editor/InsertFigureDialog.vue';
import InsertTableDialog from '@/components/editor/InsertTableDialog.vue';
import type { ReferenceItem } from '@/components/references/ReferenceListItem.vue';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useCitedReferences } from '@/composables/useCitedReferences';
import { useDocumentCounters } from '@/composables/useDocumentCounters';
import { BibliographyExtension } from '@/lib/editor/bibliography-extension';
import { CitationExtension } from '@/lib/editor/citation-extension';
import { CommentExtension } from '@/lib/editor/comment-extension';
import { ExtractionTableExtension } from '@/lib/editor/extraction-table-extension';
import type { ExtractionSnapshot } from '@/lib/editor/extraction-table-extension';
import { FigureExtension } from '@/lib/editor/figure-extension';
import { FontSizeExtension } from '@/lib/editor/font-size-extension';
import {
    FootnoteListExtension,
    FootnoteRefExtension,
} from '@/lib/editor/footnote-extension';
import {
    MathBlockExtension,
    MathInlineExtension,
} from '@/lib/editor/math-extension';
import { NumberedTableExtension } from '@/lib/editor/numbered-table-extension';
import { PageBreakExtension } from '@/lib/editor/page-break-extension';
import { PrismaExtension } from '@/lib/editor/prisma-extension';
import type { PrismaSnapshot } from '@/lib/editor/prisma-extension';
import { TocExtension } from '@/lib/editor/toc-extension';
import { VerticalSpacerExtension } from '@/lib/editor/vertical-spacer-extension';
import { snapshot as prismaSnapshot } from '@/routes/projects/prisma';
import EditorToolbar from './EditorToolbar.vue';

export interface HeadingItem {
    level: number;
    text: string;
    pos: number;
}

export interface AssetItem {
    id: string;
    original_name: string;
    mime: string;
    size_bytes: number;
    caption?: string | null;
    alt?: string | null;
    src: string;
    created_at?: string;
}

export interface ExtractionGridItem {
    id: string;
    rows_count: number;
    columns: Array<{ id: string; name: string; type: string }>;
}

const props = withDefaults(
    defineProps<{
        initialContent: string;
        references?: ReferenceItem[];
        citationStyle?: string;
        numberingEnabled?: boolean;
        projectId: string;
        assets?: AssetItem[];
        extractionGrid?: ExtractionGridItem | null;
        /** Read-only rendering (print/export view) — disables editing but keeps every NodeView rendering the same content. */
        editable?: boolean;
    }>(),
    { editable: true },
);

const emit = defineEmits<{
    update: [html: string];
    headings: [items: HeadingItem[]];
    citedIdsChange: [ids: ReadonlySet<string>];
    /** A comment highlight in the document was clicked — open its thread in the side panel. */
    openThread: [commentId: string];
    /** The "Commenter" toolbar button was clicked with a non-empty selection — carries the selected text as the thread's anchor. */
    requestComment: [selectedText: string];
}>();

let contentDebounce: ReturnType<typeof setTimeout>;

// References ref for provide/inject into NodeViews
const referencesRef = ref(props.references ?? []);

/**
 * Parse the raw content string.
 * - If it is a TipTap JSON document (starts with {"type":"doc"...}), return the parsed object
 *   so TipTap loads it as a structured document (citation + bibliography nodes are recognised).
 * - Otherwise, return the string as-is so TipTap parses it as HTML (legacy documents).
 */
function parseContent(raw: string | null): string | Record<string, unknown> {
    if (!raw) {
        return '';
    }

    try {
        const parsed = JSON.parse(raw) as Record<string, unknown>;

        if (parsed && parsed.type === 'doc') {
            return parsed;
        }
    } catch {
        // not JSON — fall through to HTML
    }

    return raw;
}

const editor = useEditor({
    content: parseContent(props.initialContent),
    editable: props.editable,
    extensions: [
        StarterKit.configure({
            heading: { levels: [1, 2, 3, 4, 5, 6] },
        }),
        TextStyle,
        Color,
        TextAlign.configure({
            types: ['heading', 'paragraph'],
        }),
        FontSizeExtension,
        PageBreakExtension,
        VerticalSpacerExtension,
        Table.configure({
            resizable: true,
            allowTableNodeSelection: true,
            HTMLAttributes: { class: 'editor-table' },
        }),
        TableRow,
        TableHeader,
        TableCell.extend({
            addAttributes() {
                return {
                    ...this.parent?.(),
                    align: {
                        default: null,
                        parseHTML: (el) => el.getAttribute('data-align'),
                        renderHTML: (attrs) =>
                            attrs.align
                                ? {
                                      'data-align': attrs.align,
                                      style: `text-align: ${attrs.align}`,
                                  }
                                : {},
                    },
                    background: {
                        default: null,
                        parseHTML: (el) => el.getAttribute('data-background'),
                        renderHTML: (attrs) =>
                            attrs.background
                                ? { 'data-background': attrs.background }
                                : {},
                    },
                };
            },
        }),
        NumberedTableExtension,
        FigureExtension,
        ExtractionTableExtension,
        MathInlineExtension,
        MathBlockExtension,
        FootnoteRefExtension,
        FootnoteListExtension,
        TocExtension,
        PrismaExtension,
        CitationExtension,
        BibliographyExtension,
        CommentExtension,
    ],
    editorProps: {
        attributes: {
            class: 'prose prose-base dark:prose-invert max-w-none min-h-[60vh] focus:outline-none',
        },
    },
    onUpdate({ editor: e }) {
        // Emit content for auto-save — always serialise as TipTap JSON so that
        // DocumentCitationsService can parse citation nodes server-side.
        clearTimeout(contentDebounce);
        contentDebounce = setTimeout(
            () => emit('update', JSON.stringify(e.getJSON())),
            1000,
        );

        // Emit updated outline headings
        emitHeadings(e);
    },
    onCreate({ editor: e }) {
        emitHeadings(e);
    },
    onSelectionUpdate({ editor: e }) {
        const { from, to } = e.state.selection;
        hasTextSelection.value = to > from;
    },
});

// Whether the current selection is non-empty — drives the "Commenter"
// toolbar button's enabled state. useEditor()'s onSelectionUpdate
// callback above closes over this; it only actually runs once the user
// interacts with the editor, by which point this is initialised.
const hasTextSelection = ref(false);

// Track which papers are currently cited
const { citedIds, citedOrder } = useCitedReferences(editor);

// Live sequential counters for numbered elements (tables, figures…)
const { tableCounters, figureCounters } = useDocumentCounters(editor);

// Reactive asset list — updated when user uploads via InsertFigureDialog
const assetsRef = ref<AssetItem[]>(props.assets ?? []);

// Provide references + citedIds + citedOrder + style for NodeViews (BibliographyNodeView, CitationNodeView)
provide('editorReferences', referencesRef);
provide('editorCitedIds', readonly(citedIds));
provide('editorCitedOrder', readonly(citedOrder));
provide('editorCitationStyle', props.citationStyle ?? 'apa');
provide(
    'editorNumberingEnabled',
    computed(() => props.numberingEnabled ?? false),
);
provide('editorProjectId', props.projectId);
provide('editorTableCounters', tableCounters);
provide('editorFigureCounters', figureCounters);
provide('editorAssets', assetsRef);

function emitHeadings(e: CoreEditor | null) {
    if (!e) {
        return;
    }

    const items: HeadingItem[] = [];
    e.state.doc.descendants((node, pos) => {
        if (node.type.name === 'heading') {
            items.push({
                level: node.attrs.level,
                text: node.textContent || '(sans titre)',
                pos,
            });
        }

        return true;
    });
    emit('headings', items);
}

const outline = computed<HeadingItem[]>(() => {
    if (!editor.value) {
        return [];
    }

    const items: HeadingItem[] = [];
    editor.value.state.doc.descendants((node, pos) => {
        if (node.type.name === 'heading') {
            items.push({
                level: node.attrs.level,
                text: node.textContent || '(sans titre)',
                pos,
            });
        }

        return true;
    });

    return items;
});

function jumpTo(pos: number) {
    if (!editor.value) {
        return;
    }

    editor.value.commands.focus(pos);
    const dom = editor.value.view.domAtPos(pos).node as HTMLElement;

    if (dom && 'scrollIntoView' in dom) {
        dom.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
}

function insertContent(text: string) {
    if (!editor.value) {
        return;
    }

    editor.value.chain().focus().insertContent(text).run();
}

function insertCitation(paperId: string, paperKey: string, label: string) {
    if (!editor.value) {
        return;
    }

    editor.value.commands.insertCitation({ paperId, paperKey, label });

    // After inserting the first citation, ensure the bibliography block exists.
    editor.value.commands.insertBibliography();
}

// ── Insert table dialog ────────────────────────────────────────────────
const showTableDialog = ref(false);

function insertNumberedTable(opts: {
    rows: number;
    cols: number;
    caption: string;
}) {
    editor.value?.chain().focus().insertNumberedTable(opts).run();
    showTableDialog.value = false;
}

// ── Insert figure dialog ───────────────────────────────────────────────
const showFigureDialog = ref(false);

function insertFigure(opts: { assetId: string; alt: string; caption: string }) {
    editor.value?.chain().focus().insertFigure(opts).run();
    showFigureDialog.value = false;
}

function onAssetUploaded(asset: AssetItem) {
    assetsRef.value = [asset, ...assetsRef.value];
}

// ── Insert extraction-table dialog ────────────────────────────────────
const showExtractionTableDialog = ref(false);

function insertExtractionTable(opts: {
    gridId: string;
    columnIds: string[];
    filter: 'all' | 'validated';
    caption: string;
    snapshotData: ExtractionSnapshot;
}) {
    editor.value?.chain().focus().insertExtractionTable(opts).run();
    showExtractionTableDialog.value = false;
}
// ── Math (inline / block) ─────────────────────────────────────────
function insertMathInline() {
    editor.value?.chain().focus().insertMathInline({ latex: '' }).run();
}

function insertMathBlock() {
    editor.value?.chain().focus().insertMathBlock({ latex: '' }).run();
}

// ── Footnote ──────────────────────────────────────────────────────
function insertFootnote() {
    editor.value?.chain().focus().insertFootnoteRef({ content: '' }).run();
}

// ── TOC ───────────────────────────────────────────────────────────
function insertToc() {
    editor.value?.chain().focus().insertToc().run();
}

// ── PRISMA diagram ───────────────────────────────────────────────
async function insertPrisma() {
    if (!editor.value) {
        return;
    }

    // Try to fetch a fresh snapshot immediately; insert an empty node if it fails
    let stats: PrismaSnapshot | null = null;
    let generatedAt: string | null = null;

    try {
        const res = await fetch(prismaSnapshot.url(props.projectId), {
            headers: { Accept: 'application/json' },
        });

        if (res.ok) {
            const data: PrismaSnapshot = await res.json();
            stats = data;
            generatedAt = data.generated_at;
        }
    } catch {
        // Insert empty node; user can regenerate manually
    }

    editor.value
        .chain()
        .focus()
        .insertPrisma({ stats, generatedAt, projectId: props.projectId })
        .run();
}

// ── Comments (anchored to a text selection) ─────────────────────────────

/** Text currently selected in the editor — used as the thread's anchor snapshot. */
function getSelectedText(): string {
    if (!editor.value) {
        return '';
    }

    const { from, to } = editor.value.state.selection;

    return editor.value.state.doc.textBetween(from, to, ' ');
}

/** Wraps the current selection with a highlight linked to an already-created thread. */
function applyCommentMark(commentId: string) {
    editor.value?.chain().focus().setComment(commentId).run();
}

/** Strips a comment's highlight from wherever it appears in the document (thread deleted). */
function removeCommentMark(commentId: string) {
    editor.value?.commands.unsetComment(commentId);
}

// Clicking a comment highlight opens its thread in the side panel. Plain
// marks have no NodeView/component of their own to attach a handler to,
// so this listens on the editor's root DOM node instead.
function onEditorDomClick(event: MouseEvent) {
    const target = (event.target as HTMLElement | null)?.closest(
        '[data-comment-id]',
    );
    const commentId = target?.getAttribute('data-comment-id');

    if (commentId) {
        emit('openThread', commentId);
    }
}

watch(
    editor,
    (e, previous) => {
        previous?.view.dom.removeEventListener('click', onEditorDomClick);
        e?.view.dom.addEventListener('click', onEditorDomClick);
    },
    { immediate: true },
);

defineExpose({
    editor,
    jumpTo,
    insertContent,
    insertCitation,
    outline,
    citedIds,
    citedOrder,
    hasTextSelection,
    showTableDialog,
    showFigureDialog,
    showExtractionTableDialog,
    getSelectedText,
    applyCommentMark,
    removeCommentMark,
});

onBeforeUnmount(() => {
    editor.value?.view.dom.removeEventListener('click', onEditorDomClick);
    editor.value?.destroy();
});
</script>

<template>
    <main
        class="flex flex-col bg-background"
        :class="props.editable ? 'min-h-0 overflow-hidden' : ''"
    >
        <EditorToolbar
            v-if="props.editable"
            :editor="editor"
            :has-selection="hasTextSelection"
            @insert-table="showTableDialog = true"
            @insert-figure="showFigureDialog = true"
            @insert-extraction-table="showExtractionTableDialog = true"
            @insert-math-inline="insertMathInline"
            @insert-math-block="insertMathBlock"
            @insert-footnote="insertFootnote"
            @insert-toc="insertToc"
            @insert-prisma="insertPrisma"
            @insert-comment="emit('requestComment', getSelectedText())"
        />

        <!-- Read-only (print): plain flow, no fixed-viewport scroll area — the
        page must grow naturally so the browser's own print pagination works. -->
        <component
            :is="props.editable ? ScrollArea : 'div'"
            class="min-h-0 flex-1"
        >
            <div
                class="relative mx-auto max-w-2xl px-12 py-12"
                :class="{ 'numbering-enabled': props.numberingEnabled }"
            >
                <EditorContent :editor="editor" class="editor-canvas-content" />
                <p
                    v-if="props.editable && editor?.isEmpty"
                    class="pointer-events-none absolute top-12 right-12 left-12 text-base text-muted-foreground/40 select-none"
                >
                    Commencez à rédiger votre revue de littérature…
                </p>
            </div>
        </component>

        <InsertTableDialog
            :open="showTableDialog"
            @insert="insertNumberedTable"
            @close="showTableDialog = false"
        />

        <InsertFigureDialog
            :open="showFigureDialog"
            :project-id="props.projectId"
            :assets="assetsRef"
            @insert="insertFigure"
            @uploaded="onAssetUploaded"
            @close="showFigureDialog = false"
        />

        <InsertExtractionTableDialog
            :open="showExtractionTableDialog"
            :project-id="props.projectId"
            :extraction-grid="props.extractionGrid ?? null"
            @insert="insertExtractionTable"
            @close="showExtractionTableDialog = false"
        />
    </main>
</template>
